import { Component, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { debounce } from 'underscore';

import { uniq } from 'rev-shared/util';
import { FileUtil } from 'rev-shared/util/FileUtil';
import { FileWrapper } from 'rev-shared/ui/fileUpload/FileWrapper';
import { LayoutDefs, LayoutType, LayoutTypes } from 'rev-shared/webrtc/Layouts';

import { ThemeService } from 'rev-portal/branding/Theme.Service';
import { IProducerBgImage, WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';

import { DefaultProducerBackground, BannerTheme, BannerTiming, IProducerBannerOptions, IProducerLayoutVideo, IProducerOptions } from './Contract';
import { getDefaultBgImgUrl, ProducerService } from './Producer.Service';

import styles from './ProducerSidebar.module.less';

@Component({
	selector: 'producer-sidebar',
	templateUrl: './ProducerSidebar.Component.html',
	host: {
		'[class]': 'styles.root'
	},
	standalone: false
})
export class ProducerSidebarComponent {
	@Input() public webcast: WebcastModel;

	public readonly styles = styles;
	public readonly imgExtensions = FileUtil.jpgPngAccept;
	public readonly maxLayoutFilter = 6;
	public readonly layoutFilterOpts = [undefined, ...range(0, this.maxLayoutFilter + 1)];
	public readonly BannerTiming = BannerTiming;
	public readonly BannerTheme = BannerTheme;
	public readonly sub = new Subscription();
	public readonly onBannerAccentChange = debounce(() => this.updateBannerOpts(), 1000);
	public readonly maxBgImages = 25;
	private processingImages: number = 0;
	private invalidFileType: boolean;

	public layoutFilter: number;
	public layoutCollapsed = false;
	public activeLayout: LayoutType = LayoutType.Empty;
	public backgroundCollapsed: boolean;
	public bannersCollapsed: boolean;
	public activeBackground: string;
	public filteredLayouts: LayoutType[];
	public bannerOpts: IProducerBannerOptions;
	public videoBackground: IProducerLayoutVideo;
	public defaultBackgrounds: DefaultProducerBackground[];

	public readonly LayoutTypes = LayoutTypes;

	constructor(
		private readonly ProducerService: ProducerService,
		public readonly ThemeService: ThemeService
	){}

	public ngOnInit(): void {
		this.updateLayouts();
		this.sub.add(this.webcast.webcast$.subscribe(webcast => {
			const producerOptions: IProducerOptions = webcast.producerOptions || {} as any;
			this.activeLayout = producerOptions.backstageLayout?.name;
			this.activeBackground = producerOptions.backstageLayout?.backgroundId;
			this.videoBackground = producerOptions.backstageLayout.backgroundVideo;
			this.bannerOpts = { ...producerOptions.banner };
			this.defaultBackgrounds = Object.values(DefaultProducerBackground).filter(bg => !producerOptions.deletedDefaultBgs.includes(bg));
		}));
	}

	public getImg(layoutType: LayoutType): string {
		return `/img/producerLayouts/${layoutType}.svg`;
	}

	public getBgImg(bg: string): string {
		return `url(${getDefaultBgImgUrl(bg)})`;
	}

	public setLayout(layoutType: LayoutType): void {
		this.ProducerService.setBackstageLayout(layoutType);
	}

	public updateLayouts(): void {
		this.filteredLayouts = this.LayoutTypes
			.filter(layout => this.layoutFilter === undefined ||
				LayoutDefs[layout]?.length === this.layoutFilter ||
				this.layoutFilter === this.maxLayoutFilter &&
					LayoutDefs[layout]?.length >= this.maxLayoutFilter);
	}

	public toggleLayoutFilter(filter: number): void {
		this.layoutFilter = filter !== this.layoutFilter ? filter : undefined;
		this.updateLayouts();
	}

	public setBackground(backgroundId: string): void {
		this.ProducerService.setDefaultBackground(backgroundId)
			.catch(e => console.error('ProducerSidebarComponent.setBackground(): ', e));
	}

	public setCustomBackground(img: IProducerBgImage): void {
		this.ProducerService.setCustomBackground(img)
			.catch(e => console.error('ProducerSidebarComponent.setCustomBackground(): ', e));
	}

	public updateBannerOpts(): void {
		this.saveBannerOpts(this.bannerOpts);
	}

	public setBannerTheme(theme: BannerTheme) {
		this.saveBannerOpts({
			...this.bannerOpts,
			theme
		});
	}

	public clearBackground(): void {
		this.ProducerService.clearBackground()
			.catch(e => console.error('ProducerSidebarComponent.clearBackgrounds(): ', e));
	}

	private saveBannerOpts(banner: IProducerBannerOptions): void {
		this.ProducerService.saveProducerSettings({
			...this.webcast.producerOptions,
			banner
		});
	}

	public customBgImgUrl(img: IProducerBgImage): string {
		const url = img.extraSmall || img.small || img.original;
		return url ? `url(${url})` : '';
	}

	public canAddBgImg(): boolean {
		return this.webcast.producerBgImages.length + this.processingImages < this.maxBgImages;
	}

	public addBgImage(file: FileWrapper): void {
		if(!this.canAddBgImg()) {
			return;
		}

		this.invalidFileType = !FileUtil.isJpgPngFile(file.extension);
		if(this.invalidFileType){
			return;
		}

		this.processingImages++;
		this.ProducerService.addBackgroundImage(this.webcast.id, file)
			.catch(e => console.error('Error adding bg image: ', e))
			.finally(() => this.processingImages--);

	}

	public removeBgImage(img: IProducerBgImage): void {
		if(!this.canDeleteBg) {
			return;
		}

		this.ProducerService.removeBackgroundImage(this.webcast.id, img.id)
			.catch(e => console.error('Error removing bg image: ', e));
	}

	public removeDefaultBg(bg: DefaultProducerBackground): void {
		if(!this.canDeleteBg) {
			return;
		}

		this.ProducerService.saveProducerSettings({
			...this.webcast.producerOptions,
			deletedDefaultBgs: uniq([
				...this.webcast.producerOptions.deletedDefaultBgs,
				bg
			])
		})
			.catch(e => console.error('Error removing bg image: ', e));
	}

	public canDeleteBg(bg: string) {
		const producerOptions = this.webcast.producerOptions;
		return bg !== producerOptions.backstageLayout.backgroundId &&
			bg !== producerOptions.onStageLayout.backgroundId;
	}
}

function range(start: number, end: number): number[] {
	return Array.from({ length: end - start }, (_, i) => i + start);
}
